import React, { useMemo } from 'react';
import SubmissionRow from './SubmissionRow';
import QualificationSubmissionDetails from './QualificationSubmissionDetails';
import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/outline';
import { Submission } from 'types/evaluation';
import { calculateAverageRank } from 'utils/submissionSorting';

const NO_RANK_SENTINEL = 999;

const QualificationSubmissionsTable = ({
  submissions,
  expandedRows,
  toggleRowExpansion,
  sortField,
  sortDirection,
  handleSort,
  getMainDescription,
  rubrics,
  showStageAction = false,
  showActionMaterial = true,
  onStageChange,
}: {
  submissions: Submission[];
  expandedRows: Set<string | number>;
  toggleRowExpansion: (id: string | number) => void;
  sortField: string;
  sortDirection: 'asc' | 'desc';
  handleSort: (field: string) => void;
  getMainDescription: (submission: Submission) => string;
  rubrics: any[];
  showStageAction?: boolean;
  showActionMaterial?: boolean;
  onStageChange?: (documentId: string, newStage: string) => void;
}) => {
  const rankMap = useMemo(() => {
    const map = new Map<string, number | null>();
    for (const submission of submissions) {
      const rank = calculateAverageRank(submission, submissions);
      map.set(submission.id, rank === NO_RANK_SENTINEL ? null : rank);
    }
    return map;
  }, [submissions]);

  const SortIndicator = ({ field }) => {
    if (sortField !== field) {
      return <ArrowUpIcon className="w-3 h-3 text-gray-300 opacity-50" />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUpIcon className="w-3 h-3 text-white" />
    ) : (
      <ArrowDownIcon className="w-3 h-3 text-white" />
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full table-fixed min-w-[1200px] xl:min-w-full">
          <thead className="bg-gradient-to-r from-gray-800 to-gray-700">
            <tr>
              <th className="w-12 py-4 px-2"></th>
              <th
                className="text-left py-4 px-2 font-baijamjuree font-semibold text-sm text-white border-r border-gray-600"
                style={{ width: '20%' }}
              >
                <button
                  onClick={() => handleSort('title')}
                  className="flex items-center gap-1 hover:text-gray-200 transition-colors"
                >
                  Project/Profile Name <SortIndicator field="title" />
                </button>
              </th>
              <th
                className="text-left py-4 px-4 font-baijamjuree font-semibold text-sm text-white border-r border-gray-600"
                style={{ width: '22%' }}
              >
                <button
                  onClick={() => handleSort('email')}
                  className="flex items-center gap-1 hover:text-gray-200 transition-colors"
                >
                  Email <SortIndicator field="email" />
                </button>
              </th>
              <th
                className="text-left py-4 px-4 font-baijamjuree font-semibold text-sm text-white border-r border-gray-600"
                style={{ width: '18%' }}
              >
                <button
                  onClick={() => handleSort('pillar')}
                  className="flex items-center gap-1 hover:text-gray-200 transition-colors"
                >
                  Pillar <SortIndicator field="pillar" />
                </button>
              </th>
              <th
                className="text-left py-4 px-4 font-baijamjuree font-semibold text-sm text-white border-r border-gray-600"
                style={{ width: '20%' }}
              >
                <button
                  onClick={() => handleSort('organization_unit')}
                  className="flex items-center gap-1 hover:text-gray-200 transition-colors"
                >
                  Organization <SortIndicator field="organization_unit" />
                </button>
              </th>
              <th
                className="text-center py-4 px-4 font-baijamjuree font-semibold text-sm text-white border-r border-gray-600"
                style={{ width: '10%' }}
              >
                <button
                  onClick={() => handleSort('avg_rank')}
                  className="flex items-center gap-1 hover:text-gray-200 transition-colors mx-auto"
                >
                  Avg Rank <SortIndicator field="avg_rank" />
                </button>
              </th>
              <th
                className={`text-center py-4 px-4 font-baijamjuree font-semibold text-sm text-white ${showStageAction ? 'border-r border-gray-600' : ''}`}
                style={{ width: '10%' }}
              >
                <button
                  onClick={() => handleSort('total_score')}
                  className="flex items-center gap-1 hover:text-gray-200 transition-colors mx-auto"
                >
                  Score <SortIndicator field="total_score" />
                </button>
              </th>
              {showStageAction && (
                <th
                  className="text-center py-4 px-4 font-baijamjuree font-semibold text-sm text-white border-r border-gray-600"
                  style={{ width: '10%' }}
                >
                  Stage
                </th>
              )}
              {showActionMaterial && (
                <>
                  <th
                    className="text-center py-4 px-4 font-baijamjuree font-semibold text-sm text-white border-r border-gray-600"
                    style={{ width: '10%' }}
                  >
                    Action
                  </th>
                  <th
                    className="text-center py-4 px-4 font-baijamjuree font-semibold text-sm text-white"
                    style={{ width: '12%' }}
                  >
                    Material
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {submissions.map((submission: Submission, index: number) => (
              <React.Fragment key={submission.id}>
                <SubmissionRow
                  index={index}
                  submission={submission}
                  expanded={expandedRows.has(submission.id)}
                  toggleRowExpansion={toggleRowExpansion}
                  averageRank={rankMap.get(submission.id)}
                  showRanking={true}
                  showStageAction={showStageAction}
                  showActionMaterial={showActionMaterial}
                  fromPage="qualificationToFinal"
                  onStageChange={onStageChange}
                />
                {expandedRows.has(submission.id) && (
                  <QualificationSubmissionDetails
                    submission={submission}
                    getMainDescription={getMainDescription}
                    rubrics={rubrics}
                    allSubmissions={submissions}
                  />
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default QualificationSubmissionsTable;
